import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const UploadData = ({
    handleNext,
    handleBack,
    selectedFile,
    setSelectedFile,
    fileName,
    setFileName,
    fileSize,
    setFileSize,
    isSubmitting,
    setIsSubmitting,
    modelName,
    selectedCountry,
    selectedOption,
    selectedUserId,
    isNewModel,
    dataOption,
    setDataOption,
}) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!isNewModel) {
            setDataOption('keep');
        } else {
            setDataOption('upload');
        }
    }, [isNewModel, setDataOption]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDataOptionChange = (e) => {
        const newOption = e.target.value;
        setDataOption(newOption);
        setSelectedFile(null);
        setFileName(null);
        setFileSize(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const maxFileSize = 1024 * 1024 * 1024; // 1GB

        if (file && file.size > maxFileSize) {
            alert('File size exceeds 1GB');
            setSelectedFile(null);
            setFileName(null);
        } else if (file && !file.name.endsWith('.csv')) {
            alert('File type must be CSV');
            setSelectedFile(null);
            setFileName(null);
        } else {
            setSelectedFile(file);
            setFileName(file.name);
            setFileSize(file.size / 1024 / 1024); // in MB
        }
    };

    const handleSampleUpload = () => {
        const isDisaggregated = selectedOption === 'Disaggregated';
        const sampleFileName = isDisaggregated ? 'disaggregated_sample.csv' : 'aggregated_sample.csv';
        const sampleFilePath = `/assets/sample-data/${sampleFileName}`;
        const endpoint = isDisaggregated ? 'upload-disaggregation-csv' : 'upload-csv';
        const url = `https://flexibility-backend.dedalus.epu.ntua.gr/${endpoint}/?user_id=${selectedUserId}&country=${selectedCountry}&model_name=${modelName}`;

        setIsSubmitting(true);

        fetch(sampleFilePath)
            .then(response => {
                if (!response.ok) throw new Error('Sample file not found');
                return response.blob();
            })
            .then(blob => {
                const file = new File([blob], sampleFileName, { type: 'text/csv' });
                const formData = new FormData();
                formData.append('file', file);

                return fetch(url, {
                    method: 'POST',
                    headers: { accept: 'application/json' },
                    body: formData,
                }).then(response => {
                    if (!response.ok) {
                        return response.text().then(msg => {
                            throw new Error(`Upload failed: ${msg}`);
                        });
                    }
                    return response.json().then(() => file);
                });
            })
            .then(file => {
                setSelectedFile(file);
                setFileName(file.name);
                setFileSize(file.size / 1024 / 1024);
            })
            .catch(error => {
                alert(`Error: ${error.message}`);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleDataSubmit = () => {
        if (selectedFile) {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append('file', selectedFile);

            const isDisaggregated = selectedOption === 'Disaggregated';
            const endpoint = isDisaggregated ? 'upload-disaggregation-csv' : 'upload-csv';

            const url = `https://flexibility-backend.dedalus.epu.ntua.gr/${endpoint}/?user_id=${selectedUserId}&country=${selectedCountry}&model_name=${modelName}`;

            fetch(url, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                },
                body: formData,
            })
                .then((response) => {
                    if (!response.ok) {
                        return response.text().then((errorMessage) => {
                            throw new Error(`Network response was not ok: ${errorMessage}`);
                        });
                    }
                    return response.json();
                })
                .then((data) => {
                    console.log(data);
                    setFileName(selectedFile.name);
                    setFileSize(selectedFile.size / 1024 / 1024);
                    handleClose();
                })
                .catch((error) => {
                    alert(`Error: ${error.message}`);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        } else {
            alert('Please select a valid file');
        }
    };

    return (
        <Box>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                    row
                    aria-label="data-option"
                    name="data-option"
                    value={dataOption}
                    onChange={handleDataOptionChange}
                >
                    {!isNewModel && (
                        <FormControlLabel value="keep" control={<Radio />} label="Keep existing dataset" />
                    )}
                    <FormControlLabel value="upload" control={<Radio />} label="Upload new dataset" />
                    <FormControlLabel value="sample" control={<Radio />} label="Use sample dataset" />
                </RadioGroup>
            </FormControl>

            {(isNewModel || dataOption === 'upload' || dataOption === 'sample') && (
                <>
                    <Typography>
                        {dataOption === 'sample' ?
                            `Sample ${selectedOption.toLowerCase()} dataset is ready to upload.` :
                            'Click below to add data:'}
                    </Typography>

                    {dataOption === 'sample' ? (
                        <Button
                            sx={{ mt: 2 }}
                            onClick={handleSampleUpload}
                            variant="contained"
                            disabled={isSubmitting || !!fileName}
                        >
                            {isSubmitting ? 'Uploading...' : fileName ? 'Uploaded' : 'Upload Sample Dataset'}
                        </Button>
                    ) : (
                        <Button sx={{ mt: 2 }} onClick={handleClickOpen} variant="contained">
                            Add Data
                        </Button>
                    )}

                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>Add Data</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                To add data to your application, please upload a file from your computer.
                            </DialogContentText>
                            <input
                                accept=".csv"
                                style={{ display: 'none' }}
                                id="raised-button-file"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="raised-button-file">
                                <Button variant="contained" component="span" sx={{ mt: 2 }}>
                                    Browse
                                </Button>
                            </label>
                            {fileName && fileSize && (
                                <Box mt={2}>
                                    <Typography variant="body1" gutterBottom>
                                        <InsertDriveFileIcon /> {fileName} ({fileSize.toFixed(2)} MB)
                                    </Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button onClick={handleDataSubmit} disabled={isSubmitting}>
                                Submit
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {fileName && fileSize && (
                        <Box mt={4}>
                            <Typography variant="body1" gutterBottom>
                                <InsertDriveFileIcon /> {fileName} ({fileSize.toFixed(2)} MB)
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            <Box sx={{ mt: 2 }}>
                <Button onClick={handleBack} variant="text">
                    Back
                </Button>
                <Button
                    onClick={handleNext}
                    variant="outlined"
                    color="primary"
                    sx={{ ml: 2 }}
                    disabled={
                        isSubmitting ||
                        (dataOption === 'sample' && !fileName) ||
                        (!isNewModel && dataOption === 'upload' && !fileName)
                    }
                >
                    Next
                </Button>
            </Box>
        </Box>
    );
};

export default UploadData; 